import { SupportedChainId } from '~/constants/chains'
import { ServerGetRequestFunction } from '~/hooks/useRequests'
import {
  IAddress,
  NFTMediaType,
  NFTTokenBalance,
  NFTTokenType,
  OrgSmartAccount,
  SmartAccountRole,
} from '~/types'
import { EvmAddress } from '~/utils/address'
import { getOpenSeaURL } from '~/utils/opensea'

export type TokenBalance = {
  ownerAddress: IAddress
  amount: bigint
  chainId: SupportedChainId
  address: IAddress
  usdValue: number
  price: number | undefined
  tokenMetadata: {
    symbol: string
    decimals: number
    chainId: SupportedChainId
    address: IAddress
  }
}

type RawNftBalanceRow = {
  chainId: SupportedChainId
  contractAddress: EvmAddress.Type
  tokenId: string
  type: 'ERC721' | 'ERC1155'
  name: string | null
  description: string | null
  collection: { name: string | null; symbol: string | null }
  mediaUrl: string | null
  mediaType: 'IMAGE' | 'VIDEO' | 'AUDIO' | null
  thumbnailUrl: string | null
  spam: boolean
  balance: string
}

// Null mediaType means the row predates media support, so treat it as an image
const NFT_MEDIA_TYPES: Record<
  NonNullable<RawNftBalanceRow['mediaType']>,
  NFTMediaType
> = {
  IMAGE: NFTMediaType.image,
  VIDEO: NFTMediaType.video,
  AUDIO: NFTMediaType.audio,
}

// Server flags spam per row (global verdict + org overrides); filtering is client-side
export const fetchSmartAccountsNFTBalances = async ({
  serverGetRequest,
  owners,
  chainIds,
  orgId,
  showSpamTokens,
}: {
  serverGetRequest: ServerGetRequestFunction
  owners: OrgSmartAccount[]
  chainIds: SupportedChainId[]
  orgId: string | undefined
  showSpamTokens: boolean
}): Promise<NFTTokenBalance[]> => {
  if (chainIds.length === 0) return []

  const results = await Promise.allSettled(
    owners.map(async (owner) => {
      const response = await serverGetRequest<{ data: RawNftBalanceRow[] }>({
        path: `/api/smart_accounts/${owner.address}/balances/nfts`,
        params: { chainIds: chainIds.join(','), ...(orgId && { orgId }) },
      })

      if (!response) throw new Error('Failed to fetch NFT balances')

      return response.data.data
        .filter((row) => showSpamTokens || !row.spam)
        .map((row): NFTTokenBalance => {
          // The server returns raw provider URLs, so ipfs:// links need the gateway rewrite
          const imageURI = row.thumbnailUrl ?? row.mediaUrl
          return {
            address: row.contractAddress,
            amount: BigInt(row.balance),
            tokenId: BigInt(row.tokenId),
            name: row.name ?? row.collection.name ?? '',
            symbol: row.collection.symbol ?? '',
            imageURI: imageURI ? getImageURLFromIPFS(imageURI) : undefined,
            mediaType: row.mediaType
              ? NFT_MEDIA_TYPES[row.mediaType]
              : NFTMediaType.image,
            mediaURI: row.mediaUrl
              ? getImageURLFromIPFS(row.mediaUrl)
              : undefined,
            type:
              row.type === 'ERC1155'
                ? NFTTokenType.ERC1155
                : NFTTokenType.ERC721,
            openseaURL: getOpenSeaURL(
              row.chainId,
              row.contractAddress,
              row.tokenId,
            ),
            collectionName: row.collection.name ?? undefined,
            chainId: row.chainId,
            account: owner.address,
          }
        })
    }),
  )

  return results
    .map((result) => (result.status === 'fulfilled' ? result.value : []))
    .flat()
}

export const getImageURLFromIPFS = (
  url: string,
  ipfsGateway = 'https://ipfs.io/ipfs/',
) => {
  if (url.startsWith('ipfs://')) {
    return ipfsGateway + url.slice(7)
  }
  return url
}

// used to sort smart accounts by name
export const getSmartAccountNameRank = (account: OrgSmartAccount) => {
  if (account.role === SmartAccountRole.TREASURY) return 0
  if (account.role === SmartAccountRole.OPERATING) return 1
  return new Date(account.createdAt).getTime()
}
